// utils/pointe.js

import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

// Active le plugin permettant de parser des dates selon un format personnalisé.
dayjs.extend(customParseFormat);

/**
 * Format standard des dates manipulées dans ce module. Exemple : "25/12/2025"
 */
export const DATE_FORMAT = "DD/MM/YYYY";

/* ------------------------------------------------------------------ *
 * Classification des comptes
 * ------------------------------------------------------------------ */

/**
 * Comptes de banque = classe 5 (comptes financiers / trésorerie).
 * Seul type de compte qui change de convention de signe (voir computeMovement).
 */
export const isBankAccount = (compteGeneral) =>
  String(compteGeneral || "").startsWith("5");

/**
 * Prédicat par défaut pour reconnaître un compte propriétaire.
 *
 *  >>> A ADAPTER A TON PLAN COMPTABLE <<<
 *
 * Sert uniquement en mode NON-COMPENSATION : on ne retient alors que la part
 * créditrice de chaque propriétaire (par compte auxiliaire). En mode
 * compensation, ce prédicat n'a aucun effet (les propriétaires sont nettés
 * comme les autres comptes).
 */
const DEFAULT_OWNER_PREFIXES = ["411"];

export const isOwnerAccountDefault = (compteGeneral) =>
  DEFAULT_OWNER_PREFIXES.some((p) => String(compteGeneral || "").startsWith(p));

/**
 * Compte de régularisation (autres débiteurs/créditeurs) dont le solde doit
 * être réintégré à la pointe lorsqu'il fait partie des comptes comptabilisés.
 * Convention de signe : crédit - débit (compte non bancaire / non propriétaire).
 */
export const COMPTE_467 = "467001";

const getCompteGeneral = (ligne) =>
  String(
    ligne?.compte_general ||
    ligne?.ecriture_ligne_compte?.compte_compte_general ||
    ""
  );

const getCompteAuxiliaire = (ligne) =>
  String(ligne?.ecriture_ligne_compte?.compte_compte_auxiliaire || "").trim();

/**
 * Vrai si la ligne appartient à un compte d'honoraires (flag compte_honoraire).
 * Le flag est rattaché à la ligne par flattenSelectedLignes (à partir de la
 * liste des comptes) ; on lit aussi le compte joint en repli.
 */
const isCompteHonoraire = (ligne) =>
  ligne?.compte_honoraire === true ||
  ligne?.ecriture_ligne_compte?.compte_honoraire === true;

/* ------------------------------------------------------------------ *
 * Utilitaires numériques et dates
 * ------------------------------------------------------------------ */

export const round2 = (n) =>
  Math.round((Number(n || 0) + Number.EPSILON) * 100) / 100;

export const parseFrenchDate = (value) => dayjs(value, DATE_FORMAT, true);

export const normalizeDateKey = (value) =>
  parseFrenchDate(value).format("YYYY-MM-DD");

export const displayDate = (value) =>
  parseFrenchDate(value).format(DATE_FORMAT);

export const isBeforeRange = (dateStr, startDate) => {
  const d = parseFrenchDate(dateStr);
  return d.isValid() && d.isBefore(dayjs(startDate).startOf("day"), "day");
};

export const isInRangeInclusive = (dateStr, startDate, endDate) => {
  const d = parseFrenchDate(dateStr);
  if (!d.isValid()) return false;
  const start = dayjs(startDate).startOf("day");
  const end = dayjs(endDate).endOf("day");
  return !d.isBefore(start, "day") && !d.isAfter(end, "day");
};

export const sortLignesByDate = (lignes = []) =>
  [...lignes].sort((a, b) =>
    parseFrenchDate(a.ecriture_date_ecriture).diff(
      parseFrenchDate(b.ecriture_date_ecriture)
    )
  );

/**
 * Mouvement comptable brut (crédit - débit) arrondi à 2 décimales.
 */
export const computeMovement = (ligne) => {
  const credit = Number(ligne?.ecriture_ligne_montant_credit || 0);
  const debit = Number(ligne?.ecriture_ligne_montant_debit || 0);
  return round2(credit - debit);
};

/* ------------------------------------------------------------------ *
 * Aplatissement des lignes des comptes sélectionnés
 * ------------------------------------------------------------------ */
export const flattenSelectedLignes = (
  ecritures = [],
  selectedRowKeys = [],
  comptes = []
) => {
  const lignes = [];

  // Lookup compte_general -> compte_honoraire (le flag est porté par les
  // comptes, pas nécessairement par chaque ligne).
  const honoraireParCompte = new Map(
    (comptes || []).map((c) => [
      String(c?.compte_compte_general),
      c?.compte_honoraire === true,
    ])
  );

  ecritures.forEach((ecriture) => {
    (ecriture.ecriture_ligne || []).forEach((ligne) => {
      const compte = ligne?.ecriture_ligne_compte?.compte_compte_general;
      if (!selectedRowKeys.includes(compte)) return;

      lignes.push({
        ...ligne,
        key: ligne.ecriture_ligne_id,
        compte_general: compte,
        compte_honoraire:
          honoraireParCompte.get(String(compte)) ??
          ligne?.ecriture_ligne_compte?.compte_honoraire === true,
        ecriture_id: ecriture.ecriture_id,
        ecriture_journal_code: ecriture?.ecriture_journal?.journal_code,
        ecriture_date_ecriture: ecriture.ecriture_date_ecriture,
      });
    });
  });

  return lignes;
};

/* ------------------------------------------------------------------ *
 * Construction de la pointe
 * ------------------------------------------------------------------ *
 *
 * Convention de signe (= ta formule) :
 *   - Banque (classe 5)  : trésorerie       => débit - crédit  (= -mouvement)
 *   - Autres comptes     : compta            => crédit - débit  (= +mouvement)
 *   - Propriétaires      :
 *        * compensation      => crédit - débit (nettés, comme un autre compte)
 *        * non-compensation  => seule la part CRÉDITRICE par auxiliaire compte
 *                               (delta d'exposition : max(solde,0))
 *   - 467 trop-perçu loc. : toujours en exposition par auxiliaire (locataire),
 *                           bornée à zéro -> sa contribution ne peut jamais être
 *                           négative et ne se nette pas entre locataires.
 *   - Honoraires (agence) : compte_honoraire=true => débit - crédit (= -mouvement) :
 *                           on retire la part créditrice (honoraires acquis à
 *                           l'agence) et on rajoute la part débitrice.
 *
 * Les comptes locataires et le 480000 doivent être exclus EN AMONT
 * (sélection / pré-filtre) : ils n'arrivent jamais ici. Le 467 trop-perçu, lui,
 * n'est traité que s'il a été sélectionné (donc présent dans les lignes).
 *
 * Paramètres :
 *   - lignesEcritures        : lignes à traiter
 *   - dateDebut / dateFin     : période
 *   - compensationProprietaire: true = nettage (défaut), false = non-compensation
 *   - isCompteProprietaire    : prédicat (compteGeneral) => bool (défaut: préfixe 411)
 */
export const buildPointes = ({
                               lignesEcritures = [],
                               dateDebut,
                               dateFin,
                               compensationProprietaire = true,
                               isCompteProprietaire = isOwnerAccountDefault,
                             }) => {
  const lignesTriees = sortLignesByDate(lignesEcritures);

  // Mode non-compensation : on isole chaque propriétaire par auxiliaire.
  const nonCompensation = (compteGeneral) =>
    !compensationProprietaire && isCompteProprietaire(compteGeneral);

  const est467 = (compteGeneral) => compteGeneral === COMPTE_467;

  // Comptes traités "en exposition" : on ne retient que la part créditrice du
  // solde courant, PAR AUXILIAIRE, via max(solde, 0). Leur contribution à la
  // pointe ne peut donc jamais être négative. Concernés :
  //   - propriétaires non compensés ;
  //   - 467 trop-perçu locataire (argent détenu pour le locataire).
  const estExposition = (compteGeneral) =>
    nonCompensation(compteGeneral) || est467(compteGeneral);

  // 1) Validation + agrégation par (date, compte général [, auxiliaire])
  const mouvementsParCle = new Map();

  lignesTriees.forEach((ligne) => {
    const dateStr = ligne.ecriture_date_ecriture;
    if (!parseFrenchDate(dateStr).isValid()) {
      throw new Error(`Date comptable invalide : ${dateStr}`);
    }

    const compteGeneral = getCompteGeneral(ligne);
    // L'auxiliaire n'est discriminant que pour un compte en exposition
    // (propriétaire non compensé ou 467 trop-perçu) : on isole chaque tiers.
    const compteAuxiliaire = estExposition(compteGeneral)
      ? getCompteAuxiliaire(ligne)
      : "";

    const dateKey = normalizeDateKey(dateStr);
    const mapKey = `${dateKey}__${compteGeneral}__${compteAuxiliaire}`;

    if (!mouvementsParCle.has(mapKey)) {
      mouvementsParCle.set(mapKey, {
        dateKey,
        ecriture_date_ecriture: displayDate(dateStr),
        compte_general: compteGeneral,
        compte_auxiliaire: compteAuxiliaire,
        honoraire: isCompteHonoraire(ligne),
        mouvement: 0,
        lignesCount: 0,
      });
    }

    const current = mouvementsParCle.get(mapKey);
    current.mouvement = round2(current.mouvement + computeMovement(ligne));
    current.lignesCount += 1;
  });

  // Tri chronologique (puis compte, puis auxiliaire) : indispensable car le
  // calcul d'exposition (non-compensation) dépend du solde courant.
  const entries = [...mouvementsParCle.values()].sort((a, b) => {
    const dateDiff = dayjs(a.dateKey, "YYYY-MM-DD").diff(
      dayjs(b.dateKey, "YYYY-MM-DD")
    );
    if (dateDiff !== 0) return dateDiff;
    const compteDiff = String(a.compte_general).localeCompare(
      String(b.compte_general)
    );
    if (compteDiff !== 0) return compteDiff;
    return String(a.compte_auxiliaire).localeCompare(String(b.compte_auxiliaire));
  });

  // Soldes courants par auxiliaire (uniquement propriétaires non compensés).
  const soldesParAuxiliaire = new Map();

  /**
   * Contribution d'une entrée à la pointe, avec mise à jour de l'état
   * (soldesParAuxiliaire) pour les comptes en exposition.
   */
  const contribution = (entry) => {
    const compte = entry.compte_general;

    if (estExposition(compte)) {
      const auxKey = `${compte}::${entry.compte_auxiliaire}`;
      const oldSolde = round2(soldesParAuxiliaire.get(auxKey) || 0);
      const newSolde = round2(oldSolde + entry.mouvement);
      soldesParAuxiliaire.set(auxKey, newSolde);
      // Seule la part positive (créditrice) du tiers alimente la pointe :
      //  - propriétaire : sa créance ;
      //  - 467 : le trop-perçu réellement détenu pour le locataire (>= 0).
      return round2(Math.max(newSolde, 0) - Math.max(oldSolde, 0));
    }

    // Honoraires (agence) : on soustrait s'ils sont créditeurs et on ajoute
    // s'ils sont débiteurs => débit - crédit. Cet argent appartient à l'agence,
    // pas aux mandants : il ne fait pas partie de la pointe mandante.
    if (entry.honoraire) {
      return round2(-entry.mouvement); // débit - crédit
    }

    if (isBankAccount(compte)) {
      return round2(-entry.mouvement); // débit - crédit
    }

    return round2(entry.mouvement); // crédit - débit
  };

  // 2) Solde initial = pointe cumulée AVANT la période
  //    (mettre soldeInitial = 0 si tu ne veux QUE les mouvements de la période)
  let soldeInitial = 0;

  // 3) Agrégation journalière sur la période
  const mouvementJourMap = new Map();

  entries.forEach((entry) => {
    const avant = isBeforeRange(entry.ecriture_date_ecriture, dateDebut);
    const dans = isInRangeInclusive(
      entry.ecriture_date_ecriture,
      dateDebut,
      dateFin
    );

    // On ignore le post-période : il ne contribue ni au solde initial
    // ni à la période, et étant chronologiquement après, il n'altère rien.
    if (!avant && !dans) return;

    // contribution() met à jour les soldes d'exposition : à appeler dans
    // l'ordre chronologique, y compris pour la phase "avant la période".
    const c = contribution(entry);

    if (avant) {
      soldeInitial = round2(soldeInitial + c);
      return;
    }

    if (!mouvementJourMap.has(entry.dateKey)) {
      mouvementJourMap.set(entry.dateKey, {
        key: entry.dateKey,
        ecriture_date_ecriture: entry.ecriture_date_ecriture,
        mouvementJour: 0,
        lignesCount: 0,
      });
    }
    const current = mouvementJourMap.get(entry.dateKey);
    current.mouvementJour = round2(current.mouvementJour + c);
    current.lignesCount += entry.lignesCount;
  });

  if (!Number.isFinite(soldeInitial)) {
    throw new Error("Calcul du solde initial invalide");
  }

  // 4) Solde cumulé jour par jour
  let solde = soldeInitial;

  const pointes = [...mouvementJourMap.values()]
    .sort((a, b) => dayjs(a.key, "YYYY-MM-DD").diff(dayjs(b.key, "YYYY-MM-DD")))
    .map((entry) => {
      solde = round2(solde + entry.mouvementJour);
      if (!Number.isFinite(solde)) {
        throw new Error("Calcul du solde cumulé invalide");
      }
      return {
        key: entry.key,
        ecriture_date_ecriture: entry.ecriture_date_ecriture,
        mouvementJour: round2(entry.mouvementJour),
        solde: round2(solde),
        lignesCount: entry.lignesCount,
      };
    });

  // 5) Données graphique + pointe max
  const dataChart = pointes.map((row) => ({
    key: row.key,
    name: row.ecriture_date_ecriture,
    pv: row.solde,
  }));

  const pointeMax =
    pointes.length > 0
      ? pointes.reduce(
        (max, row) => (row.solde > max.solde ? row : max),
        pointes[0]
      )
      : null;

  return { soldeInitial, pointes, dataChart, pointeMax };
};